import { Button, Modal } from 'react-bootstrap';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { FaCopy } from 'react-icons/fa';
import { useAppSelector } from '../../../redux/store';
import tableStyleDefaultBolder from '../../../Services/tableStyleDefaultBolder';
import CustomLoader from '../../data/customLoader';
import { paginationOptions } from '../../data/PagineteInfo';
import { InputDefault } from '../../data/inputDefault';
import { notify, notifyWarning } from '../../notify/notify-component';
import { useComplementSources } from '../../../apiQuery/useComplementSources';
import { CopyKind, CopySource, filterSources, mergeItems } from './copyComplements';
import { Section } from './styled';

interface IProps {
  kind: CopyKind;
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  list: any;
  setList: any;
}

const texts: Record<
  CopyKind,
  {
    title: string;
    column: string;
    allPresent: string;
    copied: (count: number) => string;
  }
> = {
  complements: {
    title: 'Copiar observação',
    column: 'Observações',
    allPresent: 'Todas as observações desta origem já estão na lista.',
    copied: (count) =>
      count === 1 ? '1 observação copiada.' : `${count} observações copiadas.`,
  },
  associateds: {
    title: 'Copiar extras',
    column: 'Extras',
    allPresent: 'Todos os extras desta origem já estão na lista.',
    copied: (count) =>
      count === 1 ? '1 extra copiado.' : `${count} extras copiados.`,
  },
};

const sourceLabel = {
  category: 'Categoria',
  product: 'Produto',
};

export default function ModalCopyComplements({
  kind,
  show,
  setShow,
  list,
  setList,
}: IProps) {
  const storeMe = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);
  const [search, setSearch] = useState<string>('');
  const text = texts[kind];

  const {
    data: sources = [],
    isLoading,
    isError,
  } = useComplementSources({
    kind,
    storeId: storeMe?.storeActive?.id,
    enabled: show,
  });

  const filteredSources = useMemo(
    () => filterSources(sources, search),
    [sources, search]
  );

  const columns: any = [
    {
      name: 'Código',
      sortable: true,
      width: '100px',
      center: true,
      selector: (row: CopySource) => row.code,
    },
    {
      name: 'Origem',
      sortable: true,
      width: '110px',
      selector: (row: CopySource) => sourceLabel[row.source_type],
    },
    {
      name: 'Nome',
      minWidth: '190px',
      sortable: true,
      selector: (row: CopySource) => row.name?.toLowerCase(),
    },
    {
      name: text.column,
      width: '130px',
      center: true,
      sortable: true,
      selector: (row: CopySource) => row.items.length,
      cell: (row: CopySource) => (
        <span title={row.items.map((item) => item.sale_name).join(', ')}>
          {row.items.length}
        </span>
      ),
    },
    {
      name: 'Copiar',
      cell: (row: CopySource) => (
        <div className="actions">
          <FaCopy
            size={20}
            title={`Copiar ${text.column.toLowerCase()}`}
            className="action"
            onClick={() => handleCopy(row)}
            style={{ marginRight: 16, cursor: 'pointer' }}
            color="#3C7EDF"
          />
        </div>
      ),
      button: true,
    },
  ];

  const handleClose = () => {
    setShow(false);
    setSearch('');
  };

  const handleCopy = (source: CopySource) => {
    const { list: merged, added } = mergeItems(list, source.items);

    if (added === 0) {
      notifyWarning(text.allPresent);
      return;
    }

    setList(merged);
    notify(text.copied(added));
    handleClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      style={{ fontSize: '12px' }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h3>{text.title}</h3>
        </Modal.Title>
      </Modal.Header>
      <div>
        <Modal.Body>
          <Section>
            <InputDefault
              label="Categoria ou produto"
              placeholder="Nome ou código"
              type="text"
              state={search}
              setState={setSearch}
            />
          </Section>
          <DataTable
            columns={columns}
            data={filteredSources}
            striped
            pagination
            paginationDefaultPage={1}
            paginationPerPage={10}
            customStyles={tableStyleDefaultBolder}
            paginationComponentOptions={paginationOptions}
            progressComponent={<CustomLoader />}
            progressPending={isLoading}
            noDataComponent={
              isError
                ? 'Não foi possível carregar a lista para cópia.'
                : 'Nenhuma informação foi encontrada.'
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Fechar
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
}
